import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/lib/realtime";
import { ChevronRight, Play, CheckCircle2, MapPin, Bus, GraduationCap, Loader2 } from "lucide-react";

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function DriverMissions() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [schoolServices, setSchoolServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        api.get("/missions"),
        api.get(`/driver/my-services?date_iso=${todayIso()}`).catch(() => ({ data: { items: [] } })),
      ]);
      setMissions(m.data || []);
      setSchoolServices(s.data?.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Realtime: missions room for legacy + driver:{uid} for the school
  // assignments (single source of truth). Any change reloads the page.
  const rooms = useMemo(() => {
    const r = ["missions"];
    if (user?.id) r.push(`driver:${user.id}`);
    return r;
  }, [user?.id]);
  useRealtime(rooms, {
    "mission.created":   loadAll,
    "mission.updated":   loadAll,
    "mission.cancelled": loadAll,
    "assignment.created":        loadAll,
    "assignment.updated":        loadAll,
    "assignment.deleted":        loadAll,
    "assignment.moved":          loadAll,
    "assignment.driver_changed": loadAll,
    "assignment.upserted":       loadAll,
  });

  return (
    <div className="p-4 sm:p-6" data-testid="driver-missions-page">
      <div className="mb-5">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Aujourd&apos;hui</div>
        <h1 className="font-display font-black text-3xl tracking-tighter">Mes missions</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={24} className="animate-spin text-zinc-400" /></div>
      ) : (
        <>
          {/* School tours (planned_services) */}
          {schoolServices.length > 0 && (
            <div className="mb-6" data-testid="driver-school-services">
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap size={14} className="text-[#FFC60A]" />
                <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-600">Tournées scolaires ({schoolServices.length})</div>
              </div>
              <div className="space-y-3">
                {schoolServices.map((s) => (
                  <div key={s.id} className="border border-zinc-900 bg-white p-4" data-testid={`driver-school-${s.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-16 bg-[#FFC60A]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-lg tracking-tight truncate">
                          {s.service_code}{s.service_letter || ""} — {s.client_name || "Tournée scolaire"}
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5 font-mono">
                          {s.date} · {s.start_time}–{s.end_time} · {(s.stops || []).length} arrêt(s)
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-zinc-900 bg-zinc-50">
                            <Bus size={11} /> {s.effective_vehicle_plate || s.effective_vehicle_label || "—"}
                          </span>
                          {s.has_conflict && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-900 text-[10px] font-bold uppercase">Conflit</span>
                          )}
                          <span className="text-[10px] uppercase font-mono text-zinc-500 ml-auto">
                            {s.assignment_kind === "override" ? "Override 1 jour" : "Affectation durable"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ad-hoc missions (legacy) */}
          {missions.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-600 mb-2">Missions ad-hoc ({missions.length})</div>
              <div className="space-y-3">
                {missions.map((m) => (
                  <Link key={m.id} to={`/driver/mission/${m.id}`} data-testid={`driver-mission-${m.id}`}
                    className="block border border-zinc-900 bg-white hover:bg-zinc-50 active:scale-[0.99] transition-transform">
                    <div className="p-4 flex items-start gap-3">
                      <div className={`w-3 h-16 ${m.status === "active" ? "bg-[#FFC60A]" : m.status === "completed" ? "bg-zinc-900" : "bg-zinc-300"}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="font-display font-bold text-lg tracking-tight">{m.route?.name || "Tournée"}</div>
                          <ChevronRight size={18} />
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5 font-mono">{m.date} · {m.route?.stops?.length || 0} arrêt(s)</div>
                        <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
                          <span className={`px-2 py-0.5 border border-zinc-900 font-bold uppercase tracking-widest text-[10px] ${m.status === "active" ? "bg-[#FFC60A]" : m.status === "completed" ? "bg-zinc-900 text-white" : "bg-white"}`}>
                            {m.status}
                          </span>
                          {m.route?.school_address && <span className="inline-flex items-center gap-1 text-zinc-600"><MapPin size={12} /> {m.route.school_address}</span>}
                          {m.status === "pending" && <span className="inline-flex items-center gap-1 ml-auto"><Play size={12} /> à démarrer</span>}
                          {m.status === "completed" && <span className="inline-flex items-center gap-1 ml-auto"><CheckCircle2 size={12} /> terminée</span>}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {missions.length === 0 && schoolServices.length === 0 && (
            <div className="border border-zinc-900 p-8 text-center text-sm text-zinc-500">
              Aucune mission ni tournée assignée pour le moment.
            </div>
          )}
        </>
      )}
    </div>
  );
}
